import { max } from "d3-array";
import { scaleOrdinal } from "d3-scale";
import React from "react";
import { defineMessage, useIntl } from "react-intl";
import { LineChart } from "../charts/LineChart";
import { withFigureIO } from "../components/figure";
import { useConfig } from "../config";
import { metadata } from "../data/data_access_fig_OOSREL";
import { datasets } from "../data/data_access_fig_OOSREL_datasets";
import { getLevelName, mkGemEntityDecoder, mkOrdRegion, ordLevel, regionIdIso, useButtonGroupState, useCheckboxState, useFigureControlItems, useFigureState, useNamedEntitiesWithLevel, useRegionEntities, useSingleSelectState } from "../domain";
import { useMedia } from "../hooks";
import { insertionOrderSet, unsafeFromArray } from "../lib";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { Ar, io, O, pipe } from "../prelude";
export * from "../data/data_access_fig_OOSREL";
const DEFAULT_REGION = "Sub-Saharan Africa";
const DEFAULT_LEVEL = "primary";
const Indicator = io.keyof({
    "OFST.REL": null,
    "ROFST.REL": null,
    "SAP.REL": null
});
const indicatorLookup = {
    "OFST.REL": defineMessage({
        id: "fig.access.OOSREL.OFST.REL",
        defaultMessage: "Number of out-of-school children"
    }),
    "ROFST.REL": defineMessage({
        id: "fig.access.OOSREL.ROFST.REL",
        defaultMessage: "Rate of out-of-school children"
    }),
    "SAP.REL": defineMessage({
        id: "fig.access.OOSREL.SAP.REL",
        defaultMessage: "School age population"
    })
};
export const Data = mkGemEntityDecoder([
    "year",
    "value",
    "level"
], {
    ind_id: Indicator
});
export const Chart = ({ data })=>{
    const intl = useIntl();
    const env = useConfig();
    const screenMDown = !useMedia(breakpoints.m);
    const [, actions] = useFigureState();
    const [selectedLevel] = useButtonGroupState("level");
    const [selectedRegion] = useSingleSelectState("region");
    const [showWorld] = useCheckboxState("toggleWorld");
    const entityData_ = useNamedEntitiesWithLevel(data);
    const entityData = React.useMemo(()=>{
        return entityData_.map((x)=>{
            const ind_name = intl.formatMessage(indicatorLookup[x.ind_id]);
            return {
                ...x,
                ind_name,
                category: x.entity_type === "world" ? `${x.entity_name}, ${ind_name}` : ind_name
            };
        });
    }, [
        intl,
        entityData_
    ]);
    const regionData = useRegionEntities(entityData);
    const regionDataWithoutWorld = React.useMemo(()=>regionData.filter((x)=>x.entity_type !== "world"), [
        regionData
    ]);
    const regionControlItems = useFigureControlItems(regionDataWithoutWorld, (x)=>regionIdIso.unwrap(x.id), (x)=>x.entity_name, {
        ord: mkOrdRegion(env.regions, (x)=>x.id)
    });
    const levelControlItems = useFigureControlItems(entityData, (x)=>x.level, (x)=>getLevelName(intl, x.level), {
        ord: ordLevel
    });
    React.useEffect(()=>{
        actions.setSelectionControl("level", {
            type: "ButtonGroup",
            selected: DEFAULT_LEVEL
        }, levelControlItems);
        actions.setSelectionControl("region", {
            type: "SingleSelect",
            selected: pipe(regionControlItems, Ar.findFirst((x)=>x.value === DEFAULT_REGION), O.alt(()=>Ar.head(regionControlItems)), O.map((x)=>x.value))
        }, regionControlItems);
        actions.setToggleControl("toggleWorld", {
            type: "Checkbox",
            label: intl.formatMessage(defineMessage({
                id: "Compare to world",
                defaultMessage: "Compare to world"
            })),
            selected: false
        });
    }, [
        intl,
        actions,
        regionControlItems,
        levelControlItems
    ]);
    const chartData = React.useMemo(()=>{
        const region = pipe(regionData, Ar.filter(({ id, level })=>pipe(selectedRegion, O.fold(()=>false, (x)=>x === regionIdIso.unwrap(id) && level === selectedLevel))));
        const world = regionData.filter((x)=>x.entity_type === "world" && x.level === selectedLevel);
        return showWorld ? [
            ...region,
            ...world
        ] : region;
    }, [
        regionData,
        selectedRegion,
        selectedLevel,
        showWorld
    ]);
    const maxYear = React.useMemo(()=>max(data, (x)=>x.year), [
        data
    ]);
    // Hard-coded on demand
    const baselineYear = "2000";
    const yAxisLabel = pipe(Ar.head(chartData), O.map((x)=>{
        const entity = x.entity_name;
        const level = x.level_name;
        return intl.formatMessage(defineMessage({
            id: "fig.access.OOSREL.yUnitLabel",
            defaultMessage: "({baselineYear} = 100) {entity}, {level}"
        }), {
            baselineYear,
            entity,
            level
        });
    }), O.toUndefined);
    const legendEntries = React.useMemo(()=>insertionOrderSet(unsafeFromArray(chartData), (x)=>x.category), [
        chartData
    ]);
    const colorScale = React.useMemo(()=>scaleOrdinal(Ar.chunksOf(3)(M.discretePalette)// Careful: b or c could be undefined for different chunk sizes / palette lengths
        .flatMap(([a, b, c])=>[
                a,
                b,
                c,
                a,
                b,
                c
            ])).domain(legendEntries), [
        legendEntries
    ]);
    return <LineChart height={screenMDown ? M.chartHeight.s : M.chartHeight.l} tLabel={(s)=>s} x="year" yAnnotations={[
        {
            type: "line",
            value: 100,
            label: " "
        }
    ]} xTicks={[
        2000,
        2010,
        maxYear
    ]} yTicks={[
        0,
        50,
        150,
        200,
        250
    ]} yNice={0} timeParse="%Y" timeFormat="%Y" numberFormat=".0f" category="datum.category" yAxisLabel={yAxisLabel} colorLegend colorScale={colorScale} values={chartData} markStyle={(x)=>x.entity_type === "world" ? "muted" : "normal"} showTooltip/>;
};
export default withFigureIO({
    url: new URL("../data/data_access_fig_OOSREL.json", import.meta.url).href,
    csv: new URL("../data/data_access_fig_OOSREL.zip", import.meta.url).href,
    xlsx: new URL("../data/data_access_fig_OOSREL.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    size: "narrow",
    datasets
});
